import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

const quickLinks = [
  { label: "XM Review", href: "/xm-review", icon: "ri-file-text-line", desc: "Full in-depth broker analysis" },
  { label: "Compare Brokers", href: "/compare", icon: "ri-scales-3-line", desc: "XM vs top competitors" },
  { label: "Platform Rankings", href: "/rankings", icon: "ri-trophy-line", desc: "Q3 2026 broker leaderboard" },
  { label: "Forex Academy", href: "/academy", icon: "ri-graduation-cap-line", desc: "Learn trading fundamentals" },
];

const floatingParticles = Array.from({ length: 18 }, (_, i) => ({
  id: i,
  size: Math.random() * 3 + 1,
  x: Math.random() * 100,
  y: Math.random() * 100,
  duration: Math.random() * 8 + 6,
  delay: Math.random() * 4,
  opacity: Math.random() * 0.25 + 0.05,
}));

export default function NotFound() {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(15);
  const [paused, setPaused] = useState(false);
  const [glitchActive, setGlitchActive] = useState(false);

  // Countdown auto-redirect
  useEffect(() => {
    if (paused) return;
    if (countdown <= 0) {
      navigate("/");
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, paused, navigate]);

  // Periodic glitch effect on "404"
  useEffect(() => {
    const triggerGlitch = () => {
      setGlitchActive(true);
      setTimeout(() => setGlitchActive(false), 400);
    };
    triggerGlitch();
    const interval = setInterval(triggerGlitch, 4500);
    return () => clearInterval(interval);
  }, []);

  const circumference = 2 * Math.PI * 22;
  const strokeDashoffset = circumference - (countdown / 15) * circumference;

  return (
    <div className="relative min-h-screen bg-[#0B0F19] flex flex-col items-center justify-center overflow-hidden px-4 py-20">

      {/* ── Ambient background glows ── */}
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#7C3AED]/8 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#10B981]/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#7C3AED]/3 rounded-full blur-[200px] pointer-events-none" />

      {/* ── Floating particles ── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {floatingParticles.map((p) => (
          <div
            key={p.id}
            className="absolute rounded-full bg-[#7C3AED]"
            style={{
              width: `${p.size}px`,
              height: `${p.size}px`,
              left: `${p.x}%`,
              top: `${p.y}%`,
              opacity: p.opacity,
              animation: `float-particle ${p.duration}s ${p.delay}s ease-in-out infinite alternate`,
            }}
          />
        ))}
      </div>

      {/* ── Grid lines ── */}
      <div
        className="absolute inset-0 opacity-[0.025] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(124,58,237,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(124,58,237,0.4) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      {/* ── Main content ── */}
      <div className="relative z-10 text-center max-w-3xl mx-auto w-full">

        {/* Status badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#EF4444]/30 bg-[#EF4444]/8 mb-10">
          <span className="w-2 h-2 rounded-full bg-[#EF4444] animate-pulse" />
          <span className="text-xs font-bold text-[#EF4444] uppercase tracking-wider">Error 404 — Page Not Found</span>
        </div>

        {/* 404 Big Number */}
        <div className="relative inline-block mb-6 select-none">
          {/* Shadow layer for glitch */}
          {glitchActive && (
            <>
              <div
                className="absolute inset-0 text-[120px] md:text-[180px] lg:text-[220px] font-black leading-none text-[#7C3AED] pointer-events-none"
                style={{ transform: "translate(-3px, 2px)", opacity: 0.5, clipPath: "inset(30% 0 40% 0)" }}
              >
                404
              </div>
              <div
                className="absolute inset-0 text-[120px] md:text-[180px] lg:text-[220px] font-black leading-none text-[#10B981] pointer-events-none"
                style={{ transform: "translate(3px, -2px)", opacity: 0.4, clipPath: "inset(60% 0 10% 0)" }}
              >
                404
              </div>
            </>
          )}
          <h1
            className="text-[120px] md:text-[180px] lg:text-[220px] font-black leading-none bg-clip-text text-transparent"
            style={{
              backgroundImage: "linear-gradient(135deg, #F8FAFC 30%, #7C3AED 70%, #10B981 100%)",
              transition: glitchActive ? "none" : "all 0.3s",
            }}
          >
            404
          </h1>
        </div>

        {/* Headline */}
        <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-4 leading-tight">
          This chart went off the screen
        </h2>
        <p className="text-base md:text-lg text-[#94A3B8] max-w-xl mx-auto mb-3 leading-relaxed">
          The page you're looking for has been liquidated — moved, deleted, or maybe it never existed.
          Don't panic; your broker analysis is still just a click away.
        </p>

        {/* Countdown timer */}
        <div
          className="inline-flex items-center gap-3 px-5 py-3 rounded-full bg-[#1F2937] border border-[#374151] mb-10 cursor-pointer hover:border-[#7C3AED]/40 transition-all"
          onClick={() => setPaused((p) => !p)}
          title={paused ? "Resume countdown" : "Pause countdown"}
        >
          {/* Circular progress */}
          <svg width="28" height="28" viewBox="0 0 50 50" className="shrink-0 -rotate-90">
            <circle cx="25" cy="25" r="22" fill="none" stroke="#374151" strokeWidth="4" />
            <circle
              cx="25" cy="25" r="22"
              fill="none"
              stroke={paused ? "#F59E0B" : "#7C3AED"}
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              style={{ transition: "stroke-dashoffset 1s linear" }}
            />
          </svg>
          <span className="text-sm text-[#94A3B8]">
            {paused ? (
              <span className="text-[#F59E0B] font-semibold">Paused — click to resume</span>
            ) : (
              <>
                Redirecting to homepage in{" "}
                <span className="text-[#F8FAFC] font-bold tabular-nums">{countdown}s</span>
              </>
            )}
          </span>
          <i className={`text-sm ${paused ? "ri-play-line text-[#F59E0B]" : "ri-pause-line text-[#64748B]"}`}></i>
        </div>

        {/* Primary CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-14">
          <Link
            to="/"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#7C3AED] hover:bg-[#8B5CF6] text-white font-semibold rounded-lg transition-all hover:shadow-xl hover:shadow-[#7C3AED]/25 whitespace-nowrap"
          >
            <i className="ri-home-4-line"></i>
            Back to Homepage
          </Link>
          <button
            onClick={() => window.history.back()}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent border border-[#374151] hover:border-[#7C3AED]/50 text-[#F8FAFC] font-semibold rounded-lg transition-all hover:bg-[#1F2937] whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Go Back
          </button>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-10">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent to-[#374151]" />
          <span className="text-xs text-[#64748B] uppercase tracking-widest font-semibold whitespace-nowrap">
            Or explore these pages
          </span>
          <div className="flex-1 h-px bg-gradient-to-l from-transparent to-[#374151]" />
        </div>

        {/* Quick links */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
          {quickLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="group flex items-center gap-4 p-4 bg-[#111827] border border-[#1F2937] rounded-xl hover:border-[#7C3AED]/40 hover:bg-[#1A2235] transition-all duration-300 text-left"
            >
              <div className="w-10 h-10 rounded-lg bg-[#7C3AED]/10 flex items-center justify-center shrink-0 group-hover:bg-[#7C3AED]/20 transition-colors">
                <i className={`${link.icon} text-[#7C3AED] text-lg`}></i>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-[#F8FAFC] group-hover:text-[#8B5CF6] transition-colors truncate">
                  {link.label}
                </div>
                <div className="text-xs text-[#64748B] truncate">{link.desc}</div>
              </div>
              <i className="ri-arrow-right-line text-[#374151] group-hover:text-[#7C3AED] group-hover:translate-x-0.5 transition-all shrink-0"></i>
            </Link>
          ))}
        </div>
      </div>

      {/* Bottom watermark */}
      <div className="absolute bottom-6 left-0 right-0 flex items-center justify-center text-xs text-[#374151]">
        XMReview &mdash; Independent Forex Broker Analysis
      </div>

      {/* Particle animation keyframes */}
      <style>{`
        @keyframes float-particle {
          0%   { transform: translateY(0px) translateX(0px); }
          50%  { transform: translateY(-18px) translateX(8px); }
          100% { transform: translateY(-30px) translateX(-6px); }
        }
      `}</style>
    </div>
  );
}